import FileUploadDialog from '@/components/dialog/FileUploadDialog.vue';
import DataSelectLibraryDialog from '@/views/data/DataSelectLibraryDialog.vue';
import SyncOptionsDialog from '@/components/dialog/SyncOptionsDialog.vue';

export interface BaseResponse {
    success?: boolean;
    errMsg?: string;
    errCode?: string;
}

export interface FileUploadResponse extends BaseResponse {
    data: FileUploadData;
}

export interface FileUploadData {
    uploadedFile: UploadedFile;
}

export interface UploadedFile {
    fileKey: number | string;
    fileName: string;   // display name
    filePath: string;   // unique name
    mediaType: string;
}

export interface CommonConfirmDialogRequest {
    title: string;
    content: string;
    htmlContent?: string;
    onConfirm?: (context: any) => void;
    onCancel?: () => void;
    cancelVisible?: boolean;
}

export interface DuplicatedCaseDialogRequest {
    duplicatedCases: DuplicatedCase[];
    typeOfReport: string;  // trial case / non-trial case 구분하기 위해
    onConfirm: (context: any) => void;
    onCancel: () => void;
    hasEmptyItem: boolean;
}

export interface FileUploadDialogRequest {
    acceptPattern?: string;
    onUpload: (context: FileUploadDialog, res: any) => void;
    onClose: () => void;
    title: string;
    maxFiles: number;
}

export interface FileUploadDialogRequest {
    onUpload: (context: FileUploadDialog, res: any) => void;
    onClose: () => void;
    title: string;
    maxFiles: number;
}

export interface SponsorSelectDialogRequest {
    sponsors: UserSponsorRetrieveResponse[];
    selectedSponsorKey?: string;
    selectedSenderKey?: string;
    userKey: string;
    width?: string;
    onConfirm: (selected: SponsorSelectDialogResponse) => void;
    onClose?: () => void;
    token?: string;
}

export interface SponsorSelectDialogResponse {
    sponsorKey: string;
    sessionSenderKey: string;
}

type CallbackFunction = (res: CommonReasonDialogResponse) => void;

export interface CommonReasonDialogRequest {
    onConfirm: CallbackFunction;
    onClose?: () => void;
    title?: string;
    content?: string;
    width?: string;
}

export interface AuditTrailDialogRequest {
    auditTrailList: any[];
    columnList: TableHeader[];
    pageInfo?: PageInfo;
    width?: string;
}

export interface AuditTrailDialogResponse {
    auditTrailList: AuditTrail[];
    caseKey: number;
    rowNo: number;
    rowNo2: number;
    rowNo3: number;
}

export interface MakeEditorTableDialogRequest {
    onConfirm: (rows: number, columns: number, isWithHeaderRow: boolean) => void;
}

export interface InputUrlDialogRequest {
    previousUrl: string;
    onConfirm: (url: string) => void;
}

export interface SubmissionConfirmDialogRequest {
    cases: number;
    onConfirm: ((comments: string, hide: () => void) => void);
}

export interface SubmissionCommentUpdateDialogRequest {
    batchKey: number;
    onConfirm: () => void;
}

export interface SubmissionCommentRevisionResponse extends BaseResponse {
    data: {
        revisions: SubmissionCommentRevision[];
        manualBatchStatusOptions: CodeRetrieveResponse[];
        changeableStatus: boolean;
        originBatchStatus: string;
    };
}

export interface SubmissionCommentRevision {
    comments: string;
    status: string;
    dateTime: string;
    userId: string;
}

export interface DataSelectLibraryDialogRequest {
    onSelect: (context: DataSelectLibraryDialog, row: DataLibrariesListInfo) => void;
}

export interface CaseEmailLogDetailDialogRequest {
    emailKey: number | string;
}

export interface CaseReportEmailsRetrieveResponse {
    data: CaseReportEmailsListInfo[];
}

export interface CaseReportEmailsListInfo {
    userList: string;
}

export interface AuditTrail {
    itemId: string;
    itemLabel: string;
    notation: string;
    nullFlavor: string;
    reason: string;
    time: string;
    userId: string;
    value: string;
}

export interface CaseTopMenuAuditTrailDialogResponse {
    auditTrailList: CaseTopMenuAuditTrail[];
    caseKey: number;
}

export interface CaseTopMenuAuditTrail {
    action: string;
    pageId: string;
    pageLabel: string;
    reason: string;
    status: string;
    statusId: string;
    time: string;
    userId: string;
}

export interface CaseVersionRetrieveResponse {
    data: CaseVersionRetrieveData;
}

export interface CaseVersionRetrieveData {
    versions: CaseVersion[];
    caseNo: string;
}

export interface CaseVersion {
    index: number;
    caseKey: number;
    caseRegTime: string;
    delFlag: string;
    funo: number;
}

export interface ItemAuditTrailDialogRequest {
    auditTrailList: any[];
    columnWidths?: string[];
    itemColumns?: any[];
    separator?: string;
    isHTML?: boolean;
    width?: string;
}

export interface CommonReasonDialogResponse {
    result: boolean;
    reason: string;
}

export interface ReporterSelectDialogRequest {
    reporterKey: any;
    selectOnHide?: boolean;
    caseKey?: any;
    width?: string;
    onSelect: ($event: any, row: CaseInfoReporterListInfo) => void;
}

export interface SenderSelectDialogRequest {
    senderKey: any;
    selectOnHide?: boolean;
    caseKey?: any;
    width?: string;
    onSelect: ($event: any, row: CaseInfoSenderListInfo) => void;
}

export interface SyncCdmsStudySelectDialogRequest {
    studyKey: any;
    width?: string;
    onSelect: ($event: any, row: SyncCdmsStudyListInfo) => void;
}

export interface SyncR3StudySelectDialogRequest {
    studyKey: any;
    width?: string;
    onSelect: ($event: any, row: SyncR3StudyListInfo) => void;
}

export interface KcdSelectDialogRequest {
    version: string;
    keyword: string;
    width?: string;
    onSelect: (row: CaseKcd) => void;
    onCancel: (keyword: string) => void;
}

export interface CaseKcdRetrieveResponse extends BaseResponse {
    data: CaseKcdRetrieveData;
}

export interface CaseKcdRetrieveData {
    listInfo: CaseKcd[];
    pageInfo: PageInfo;
}

export interface CaseKcd {
    kcdTerm: string;
    kcdTermEng: string;
    kcdCode: string;
}

export interface WhoartSelectDialogRequest {
    version: string;
    keyword: string;
    width?: string;
    onSelect: (row: CaseWhoart) => void;
    onCancel: (keyword: string) => void;
}

export interface CaseWhoartRetrieveResponse extends BaseResponse {
    data: CaseWhoartRetrieveData;
}

export interface CaseWhoartRetrieveData {
    listInfo: CaseWhoart[];
    pageInfo: PageInfo;
}

export interface CaseWhoart {
    arrn: string;
    inclTerm: string;
    seq: string;
    subSeq: string;
    termEng: string;
}

export type AiCodingStatus = 'PROGRESS' | 'NOT_MATCHED' | 'SUCCESS';
export type AiCodingType = 'WHODD' | 'WHODD_SUBS' | 'MFDS' | 'MFDS_SUBS';

export enum MfdsReportType {
    CT = '1',
    CU = '2',
    KR = '3',
    FR = '4'
}

export interface MfdsProductSelectDialogRequest {
    value: string;
    width?: string;
    version: string;
    keyword: string;
    maxLength: number;
    onSelect: (row: MfdsProductListInfo) => void;
    onCancel: (keyword: string) => void;
    isAiCoding?: boolean;
}

export interface MfdsProductRetrieveResponse extends BaseResponse {
    data: MfdsProductRetrieveResponseData;
}

export interface MfdsProductRetrieveResponseData {
    listInfo: MfdsProductListInfo[];
    pageInfo: PageInfo;
}

export interface MfdsProductListInfo {
    mfdsTerm: string;
    mfdsTermEng: string;
    mfdsCode: string;
    mfdsCancelStatus: string;
    mfdsCancelDate: string;
}

export interface MfdsSubstanceSelectDialogRequest {
    value: string;
    keyword: string;
    width?: string;
    version: string;
    maxLength: number;
    onSelect: (row: MfdsSubstanceListInfo) => void;
    onCancel: (keyword: string) => void;
    rowNo2: number;
    isAiCoding: boolean;
}

export interface MfdsSubstanceRetrieveResponse extends BaseResponse {
    data: MfdsSubstanceRetrieveResponseData;
}

export interface MfdsSubstanceRetrieveResponseData {
    listInfo: MfdsSubstanceListInfo[];
    pageInfo: PageInfo;
}

export interface MfdsSubstanceListInfo {
    mfdsTerm: string;
    mfdsTermEng: string;
    mfdsCode: string;
}

export interface CaseProductSelectDialogRequest {
    keyword: string;
    width?: string;
    onSelect: (row: CaseProductListInfo) => void;
    onCancel: (keyword: string) => void;
}

export interface CaseProductRetrieveResponse extends BaseResponse {
    data: CaseProductRetrieveResponseData;
}

export interface CaseProductRetrieveResponseData {
    listInfo: CaseProductListInfo[];
    pageInfo: PageInfo;
}

export interface CaseProductListInfo {
    BRAND_NAME: string;
    DEL_FLAG: boolean;
    DESCRIPTION: string;
    MNFT_NAME: string;
    PRD_BRAND_NAME: string;
    PRD_KEY: number;
    PRD_NAME: string;
    REG_TIME: string;
    SENDER_KEY: number;
    deleted: boolean;
}

export interface KimsSelectDialogRequest {
    value: string;
    keyword: string;
    maxLength: number;
    width?: string;
    onSelect: (row: KimsListInfo) => void;
    onCancel: (keyword: string) => void;
}

export interface KimsRetrieveResponse extends BaseResponse {
    data: KimsRetrieveResponseData;
}

export interface KimsRetrieveResponseData {
    listInfo: KimsListInfo[];
    pageInfo: PageInfo;
}

export interface KimsListInfo {
    kimsEng: string;
    kimsKor: string;
    manufacturer: string;
    mfdsCode: string;
    mfdsTerm: string;
}

export enum WhoddSelectSearchFilter1 {
    DrugName = 'PRODUCT_NAME'
}

export interface WhoddRetrieveRequest {
    whoddType: string;
    version: string;
    searchText: string;
    genOrPre?: string;
    genAndPre?: string;
    exactMatch?: string;
    countryCode?: string | null;
}

export interface WhoddVersionInfo {
    tableVersion: string;
    version: string;
    shortVersion: string;
}

export interface WhoddSelectDialogRequest {
    whoddTypes: CodeRetrieveResponse[];
    maxLength: number;
    whoddVersionInfo: WhoddVersionInfo;
    reporterCdmsMappingCountry?: string | null;
    onSelect: (row: WhoddListInfo) => void;
    isAiCoding?: boolean;
}

export interface WhoddRetrieveResponse extends BaseResponse {
    data: WhoddListInfo[];
}

export interface WhoddAiCodeRetrieveResponse extends BaseResponse {
    data: WhoddAiCodeRetrieveData;
}

export interface WhoddAiCodeRetrieveData {
    recommendedCodes: WhoddListInfo[];
    status: AiCodingStatus;
}

export interface WhoddSubstanceAiCodeRetrieveResponse extends BaseResponse {
    data: WhoddSubstanceAiCodeRetrieveData;
}

export interface WhoddSubstanceAiCodeRetrieveData extends BaseResponse {
    recommendedCodes: WhoddCasListInfo[];
    status: AiCodingStatus;
}

export interface MfdsProductAiCodeRetrieveResponse extends BaseResponse {
    data: MfdsProductAiCodeRetrieveData;
}

export interface MfdsProductAiCodeRetrieveData extends BaseResponse {
    recommendedCodes: MfdsProductListInfo[];
    status: AiCodingStatus;
}

export interface MfdsSubstanceAiCodeRetrieveResponse extends BaseResponse {
    data: MfdsSubstanceAiCodeRetrieveData;
}

export interface MfdsSubstanceAiCodeRetrieveData extends BaseResponse {
    recommendedCodes: MfdsSubstanceListInfo[];
    status: AiCodingStatus;
}

export interface WhoddListInfo {
    medicinalProductId: string;
    drugCode: string;
    drugName: string;
    nameSpecifier: string;
    ingredients: string;
    generic: string;
    preferred: string;
    country: string;
    mah: string;
    pharmForm: string;
    strength: string;
    preferredName: string;
    selAtcCode: string;
    selAtcName: string;
    atcCode: string;
    atcName: string;
    bgColor: string;
    isDirectInput?: boolean;
}

export interface IdfInfoRetrieveResponse extends BaseResponse {
    idf: boolean;
    version: string;
    versionCode: string;
}

export interface IdfListInfo {
   maintenanceSeq: string;
   drugCode: string;
   frequencyCategory: string;
   useCategory2: string;
   drugName: string;
   drugNameKana: string;
   commonName: string;
   commonNameKana: string;
   version: string;
   dosageFormCode: string;
   maintenanceFlag: string;
   drugNameEng: string;
   dosageFormName: string;
}

export interface IdfRetrieveResponse extends BaseResponse {
    data: IdfListInfo[];
}

export interface IdfVersionInfo {
    idf: boolean;
    version: string;
    versionCode: string;
}

export interface CaseIdfSelectDialogRequest {
    idfVersionInfo: IdfVersionInfo;
    value: string;
    keyword: string;
    maxLength: number;
    width?: string;
    isSubstance: boolean;
    onSelect: (row: IdfListInfo) => void;
    onCancel: (keyword: string) => void;
}

export interface CaseWhoddItemDto {
    medicinalProductVersion: CaseItemDto;
    medicinalProductId: CaseItemDto;
}

export interface WhoddCasSelectDialogRequest {
    keyword: string;
    width?: string;
    whoddVersionInfo: WhoddVersionInfo;
    maxLength: number;
    onSelect: (row: WhoddCasListInfo) => void;
    onCancel: (keyword: string) => void;
    rowNo2: number;
    isAiCoding: boolean;
}

export interface WhoddCasRetrieveResponse extends BaseResponse {
    data: WhoddCasRetrieveResponseData;
}

export interface WhoddCasRetrieveResponseData {
    listInfo: WhoddCasListInfo[];
    pageInfo: PageInfo;
}

export interface WhoddCasListInfo {
    substanceName: string;
    casNumber: string;
    isDirectInput?: boolean;
}

export interface CommonPaginationResponse<T> extends BaseResponse {
    data: {
        listInfo: T[];
        pageInfo: PageInfo;
    };
}

export interface CaseDictionaryDialogRequest<T> {
    value: string;
    keyword: string;
    maxLength: number;
    width?: string;
    onSelect: (data: T) => void;
    onCancel: (keyword: string) => void;
}

// NSDE
export interface CaseNsdeListInfo {
    itemCode: string;
    ndc11: string;
    propName: string;
    doseForm: string;
    marketingCat: string;
    appNumMonoId: string;
    prdType: string;
    marketingStartDate: string;
    marketingEndDate: string;
    bilUnit: string;
    inactiveDate: string;
    reactiveDate: string;
}

export interface CaseNsdeDictionaryDialogResponse extends CommonPaginationResponse<CaseNsdeListInfo> {}
export interface CaseNsdeDictionaryDialogRequest extends CaseDictionaryDialogRequest<CaseNsdeListInfo> {}

// UNII
export interface CaseUniiListInfo {
    name: string;
    type: string;
    unii: string;
    displayName: string;
}

export interface CaseUniiDictionaryDialogResponse extends CommonPaginationResponse<CaseUniiListInfo> {}
export interface CaseUniiDictionaryDialogRequest extends CaseDictionaryDialogRequest<CaseUniiListInfo> {}

// FDA Device Problem Code
export interface CaseDeviceProblemCodeListInfo {
    level1Term: string;
    level2Term: string;
    level3Term: string;
    fdaCode: string;
    imdrfCode: string;
}

export interface CaseDeviceProblemCodeDictionaryDialogResponse extends CommonPaginationResponse<CaseDeviceProblemCodeListInfo> {}
export interface CaseDeviceProblemCodeDictionaryDialogRequest extends CaseDictionaryDialogRequest<CaseDeviceProblemCodeListInfo> {}

// FDA Device Component Code
export interface CaseDeviceComponentCodeListInfo {
    level1Term: string;
    level2Term: string;
    level3Term: string;
    fdaCode: string;
    imdrfCode: string;
}

export interface CaseDeviceComponentCodeDictionaryDialogResponse extends CommonPaginationResponse<CaseDeviceComponentCodeListInfo> {}
export interface CaseDeviceComponentCodeDictionaryDialogRequest extends CaseDictionaryDialogRequest<CaseDeviceComponentCodeListInfo> {}

export interface AiCodingUpdateConfirmDialogRequest {
    whoddVer?: string;
    targetElement?: string;
    totalRow: number;
    onConfirm?: (context: any) => void;
}

export interface ProductRecommendedCodeInfoDialogRequest {
    whoddCode: number | string;
    onMore: (context: any) => void;
}

export interface WhoddSubstanceRecommendedCodeInfoDialogRequest {
    casCode: number | string;
    onMore: (context: any) => void;
}

export interface MfdsProductRecommendedCodeInfoDialogRequest {
    drugCode: number | string;
    onMore: (context: any) => void;
}

export interface MfdsSubstanceRecommendedCodeInfoDialogRequest {
    drugCode: number | string;
    onMore: (context: any) => void;
}

export interface CaseWorkflowBatchUpdateDialogRequest {
    caseKeys: number[];
    currentWorkflow: WorkflowInfo;
    workflows: Workflow[];
    onConfirm: (context: any) => any;
}

export type SyncOption = 'ADVERSE_EVENT' | 'SUBJECT';
export interface SyncOptionsDialogRequest {
    onConfirm: (option: SyncOptionsDialog) => void;
}

export interface CaseNarrativeSelectDialogRequest {
    width?: string;
    selectOnHide?: boolean;
    onSelect: ($event: any, row: CaseNarrativeListInfo) => void;
}

export interface CaseNarrativeListInfo {
    NR_KEY: number;
    NAME: string;
}

export interface CaseAutoNarrativeRetrieveResponse extends BaseResponse {
    data: CaseAutoNarrativeRetrieveData;
}

export interface CaseAutoNarrativeRetrieveData {
    narrative: string;
    notation: string;
}

export interface CaseSubstanceDto {
    name: CaseItemDto;              // G.k.2.3.r.1
    mfdsVersion: CaseItemDto;       // G.k.2.3.r.1.KR.1a
    mfdsId: CaseItemDto;            // G.k.2.3.r.1.KR.1b
    termIdVersion: CaseItemDto;     // G.k.2.3.r.2a
    termId: CaseItemDto;            // G.k.2.3.r.2b
    strengthNo: CaseItemDto;        // G.k.2.3.r.3a
    strengthUnit: CaseItemDto;      // G.k.2.3.r.3b
    pmdaIngredient: CaseItemDto;    // J2.25.k.r
}

export enum ReceiverSelectDialogRequestType {
    DataExport = 'DataExport',
    SelectItem = 'SelectItem',
    Submission = 'Submission',
    OriginalManufacturer = 'OriginalManufacturer'
}

export interface ReceiverSelectDialogRequest {
    receiverKey: any;
    senderKey?: any;
    receiverName: any;
    type: ReceiverSelectDialogRequestType; // SelectItem, DataExport, Submission
    width?: string;
    onSelect: ($event: any, row: CaseInfoReceiverListInfo) => void;
}

export interface ProductSelectDialogRequest {
    productKey: any;
    productBrandName: any;
    useAll?: boolean;
    width?: string;
    deleteFilter?: boolean;
    onSelect: ($event: any, row: CaseInfoProductListInfo) => void;
}

export interface StudySelectDialogRequest {
    studyKey: any;
    caseKey?: any;
    selectOnHide: boolean;
    width?: string;
    onSelect: ($event: any, study: CaseInfoStudyListInfo) => void;
}

export interface MultipleProductsSelectDialogRequest {
    selectedProducts: any[];
    selectedSenderKeys: string;
    width?: string;
    onSelect: ($event: any, productKeys: CaseInfoProductListInfo[]) => void;
    deleteFilter?: boolean;
}

export interface MultipleStudiesSelectDialogRequest {
    selectedStudies: any[];
    selectedSenderKeys: string;
    width?: string;
    onSelect: ($event: any, studies: CaseInfoStudyListInfo[]) => void;
    deleteFilter?: boolean;
}

export interface MultipleSendersSelectDialogRequest {
    selectedSenders: any[];
    selectedProductKeys: string;
    selectedStudyKeys: string;
    width?: string;
    onSelect: ($event: any, studies: CaseInfoSenderListInfo[]) => void;
}

export interface MultipleRolesSelectDialogRequest {
    selectedRoles: any[];
    width?: string;
    onSelect: ($event: any, studies: AdminRolesListInfo[]) => void;
}

export interface SendPasswordResetEmailResponse {
    data: SendPasswordResetEmailData;
}

export interface SendPasswordResetEmailData {
    message: string;
    title: string;
    userId: string;
}

export interface HelpdeskTokenRetrieveResponse {
    result: HelpdeskTokenRetrieveResult;
    status: string;
}

export interface HelpdeskTokenRetrieveResult {
    redirectURL: string;
}

export interface DiscardConfirmDialogRequest {
    onConfirm: CallbackFunction;
}

export interface DiscardConfirmDialogResponse {
    result: boolean;
}

export interface SenderReceiverPickDialogRequest {
    caseKey: string;
    onCancel: () => void;
    onDownload: (res: SenderReceiverPickDialogResponse) => void;
}

export interface SenderReceiverPickDialogResponse {
    senderKey: string;
    receiver: Receiver;
}

export interface CommonSelectDialogRequest {
    title: string;
    tableLabel: string;
    itemId: string;
    width?: string;
    onSelect: (row: CodeRetrieveResponse) => void;
}

export interface ReceiverRetrieveResponse {
    data: ReceiverRetrieveData;
}

export interface ReceiverRetrieveData {
    receiverList: Receiver[];
}

export interface Receiver {
    ema: boolean;
    receiverName: string;
    receiverType: string;
    valid: boolean;
}

export interface FilterRetrieveResponse {
    data: FilterRetrieveData;
}

export interface RowCountRetrieveResponse {
    data: {
        rowCount: number;
        fullText: string;
    };
}

export interface FilterRetrieveData {
    filterList: Filter[];
    filterSource: string;
    filterType: string;
}

export interface Filter {
    selected?: string;
    filterType: string;
    valueCode?: string;
    valueCode2?: string;
    valueLabel?: string;
}

export interface TableHeader {
    columnId: string;
    columnLabel: string;
    filterType?: string;
    filtered?: string;
    orderBy?: string;
    seqNo?: number;
    sorted?: string;
    visible: string;
    columnType?: ColumnType;
    width?: string;
    fixed?: boolean;
}

export enum ColumnType {
    HTML = 'HTML'
}

export interface ColumnRetrieveResponse extends BaseResponse {
    data: ColumnResponseData;
}

export interface ColumnResponseData {
    columnList: TableHeader[];
    menuId?: string;
}

export interface HomeNoticeRetrieveResponse {
    data: HomeNoticeRetrieveData;
    success: boolean;
    errMsg: string;
}

export interface HomeNoticeRetrieveData {
    listInfo: HomeNoticeListInfo[];
    pageInfo: PageInfo;
}

export interface HomeNoticeListInfo {
    ALARM_EMAIL: string;
    ATT_FILES: string;
    DEL_FLAG: string;
    EFFECT_DATE: string;
    EXPIRE_DATE: string;
    LAST_TIME: string;
    NOTICE_ALL: string;
    NOTICE_KEY: number;
    REG_TIME: string;
    TITLE: string;
    USER_NAME: string;
    USER_NAME_REG: string;
}

export interface HomeNoticeDetailRetrieveResponse {
    data: HomeNoticeDetailRetrieveData;
    success: boolean;
    errMsg: string;
}

export interface HomeNoticeDetailRetrieveData {
    alarmEmail: string;
    content: string;
    delFlag: string;
    effectDate: string;
    expireDate: string;
    country: string | null;
    fileList: NoticeDetailFile[];
    new: boolean;
    noticeAll: string;
    noticeKey: number;
    regTime: string;
    title: string;
    userName: string;
}

export interface NoticeDetailFile {
    fileKey: number;
    fileName: string;
}

export interface HomeNoticeAuditTrailsResponse {
    data: HomeNoticeAuditTrail[];
}

export interface HomeNoticeAuditTrail {
    itemId: string;
    itemLabel: string;
    no: number;
    reason: string;
    time: string;
    userId: string;
    value: string;
}

export interface HomeCaseReportingDeadlineIsApproachingRetrieveResponse {
    data: HomeCaseReportingDeadlineIsApproachingRetrieveData;
}

export interface HomeCaseReportingDeadlineIsApproachingRetrieveData {
    listInfo: HomeCaseReportingDeadlineIsApproachingListInfo[];
    pageInfo: PageInfo;
}

export interface HomeCaseReportingDeadlineIsApproachingListInfo {
    AE_NAME: string;
    CASE_KEY: string;
    CASE_NO: string;
    DAYS: string;
    PRODUCT_NAME: string;
    RECEIVER: string;
}

export interface HomeEdcSyncStatusRetrieveResponse {
    data: HomeEdcSyncStatusRetrieveData[];
}

export interface HomeEdcSyncStatusRetrieveData {
    STD_KEY: number;
    STUDY: string;
    SYNC_COUNT: number;
}

export interface HomeWorkflowRetrieveResponse {
    data: HomeWorkflowRetrieveData;
}

export interface HomeWorkflowRetrieveData {
    statuses: HomeWorkflowStatus[];
    todos: HomeWorkflowTodo[];
    statusTotal: number;
    submissionDelayedTodoTotal: number;
    submissionMyDelayedTodoTotal: number;
}

export interface HomeWorkflowStatus {
    count: number;
    workflowLabel: string;
}

export interface HomeWorkflowTodo {
    count: number;
    overdue: number;
    workflowLabel: string;
}

export interface CaseDHSummaryResponse extends BaseResponse {
    data: CaseDHSummaryData;
}

export interface CaseDHSummaryData {
    list: CaseDhSummary[];
    pageInfo: PageInfo;
}

export interface CaseDhSummary {
    rowNo: number;
    deleted: boolean;
    drugName: string;
    mpid: string;
    startDate: string;
    endDate: string;
    indication: string;
    reaction: string;
    warningCount: number;
    CHECKED?: boolean;
}

export interface CaseLBSummaryResponse extends BaseResponse {
    data: CaseLBSummaryData;
}

export interface CaseLBSummaryData {
    list: CaseLbSummary[];
    pageInfo: PageInfo;
}

export interface CaseLbSummary {
    rowNo: number;
    deleted: boolean;
    testDate: string;
    testName: string;
    testResult: string;
    lowValue: string;
    highValue: string;
    comments: string;
    warningCount: number;
    CHECKED?: boolean;
}

export interface CaseDGSummaryResponse extends BaseResponse {
    data: CaseDGSummaryData;
}

export interface CaseDGSummaryData {
    list: CaseDgSummary[];
    pageInfo: PageInfo;
}

export interface CaseDgSummary {
    rowNo: number;
    deleted: boolean;
    drugRole: string;
    productKeyName: string;
    productName: string;
    actionTaken: string;
    warningCount: number;
    CHECKED?: boolean;
}

export interface CaseRepeatPageStatusData {
    deleted: boolean;
    rowNos: number[];
    reason?: string;
}

export interface CaseRPSummaryResponse extends BaseResponse {
    data: CaseRPSummaryData;
}

export interface CaseRPSummaryData {
    list: CaseRpSummary[];
    pageInfo: PageInfo;
}

export interface CaseRpSummary {
    rowNo: number;
    primary: string;
    organisation: string;
    givenName: string;
    qualification: string;
    country: string;
    deleted: boolean;
    CHECKED?: boolean;
}

export interface CaseLrSummary {
    rowNo: number;
    reference: string;
    documents: string;
    deleted: boolean;
    CHECKED?: boolean;
}

export interface CaseAESummaryResponse extends BaseResponse {
    data: CaseAESummaryData;
}

export interface CaseAESummaryData {
    list: CaseAeSummary[];
    pageInfo: PageInfo;
}

export interface CaseAeSummary {
    rowNo: number;
    deleted: boolean;
    reactionEvent: string;
    termHighlighted: string;
    seriousness: string;
    startDate: string;
    endDate: string;
    outcome: string;
    country: string;
    CHECKED?: boolean;
}

export interface CasesRetrieveResponse extends BaseResponse {
    data: CasesRetrieveData;
}

export interface CasesRetrieveData {
    listInfo: CaseListInfo[];
    pageInfo: PageInfo;
}

export interface CaseListInfo {
    AE_TERM: string;
    CASE_KEY: number;
    CASE_NO: string;
    CASE_RECENT_DTC: string;
    CASE_UID_WORLD: string;
    DEL_FLAG: string;
    DUE_DATE: string;
    IS_SAE: string;
    LOCKED: string;
    MANUFACTURER: string;
    PRD_NAME: string;
    REG_TIME: string;
    REPORTER: string;
    REQUIRED: string;
    REVIEWED: string;
    SND_ORGAN: string;
    SOURCE_TYPE: string;
    SUBJECT: string;
    WARNING: string;
    CHECKED?: boolean;
}

export interface CaseDuplicationCheckResponse extends BaseResponse {
    data: CaseDuplicationCheckResponseData;
}

export interface CaseDuplicationCheckResponseData {
    duplicatedCases: DuplicatedCase[];
    regTime: string;
}

export interface DuplicatedCase {
    caseNo: string;
    recentDateTime: string;
    reportType: string;
    organisation: string;
    organisationNF: string;
    sponsorStudyNumber: string;
    sponsorStudyNumberNF: string;
    patientName: string;
    patientNameNF: string;
    investigationNumber: string;
    investigationNumberNF: string;
    age: string;
    sex: string;
    sexNF: string;
    aeMeddraLlt: string;
    aeStartDate: string;
}

export interface CaseWarningssRetrieveResponse {
    data: CaseWarningssRetrieveData;
}

export interface CaseWarningssRetrieveData {
    listInfo: CaseWarningsListInfo[];
    pageInfo: PageInfo;
}

export interface CaseWarningsListInfo {
    ASE_KEY: number;
    CASE_NO: number;
    CASE_RECENT_DTC: string;
    DATA_KEY: number;
    ITEM_ID: string;
    ITEM_LABEL: string;
    PAGE_ID: string;
    PAGE_LABEL: string;
    ROW_NO: number;
    ROW_NO2: number;
    ROW_NO3: number;
    VALUE: string;
    WARNING_MESSAGE: string;
}

export interface CaseEmailLogsRetrieveResponse {
    data: CaseEmailLogsRetrieveData;
}

export interface CaseEmailLogsRetrieveData {
    listInfo: CaseEmailLogsListInfo[];
    pageInfo: PageInfo;
}

export interface CaseEmailLogsListInfo {
    CASE_EMAIL_KEY: number;
    CASE_NO: string;
    CASE_RECENT_DTC: string;
    CASE_RPT_TYPE: string;
    FILE_KEY: number;
    PRODUCT: string;
    RECIPIENTS: number;
    REG_TIME: string;
    REPORT_TYPE: string;
    SENDER: string;
    STATUS: string;
}

export interface CaseEmailLogDetailRetrieveResponse {
    data: CaseEmailLogDetailRetrieveData;
}

export interface CaseEmailLogDetailRetrieveData {
    recipients: CaseEmailReceipient[];
}

export interface CaseEmailReceipient {
    address: string;
    userName: string;
}

export interface CasePagesRetrieveResponse {
    data: CasePagesRetrieveData[];
    success: boolean;
    errCode: number;
    errorDetails: string;
    errMsg: string;
}

export interface CasePagesRetrieveData {
    caseKey: number;
    deleted: string;
    locked: string;
    reviewed: string;
    pageList: CasePage[];
    caseTabWorkflow: {
        workflowStatus: string,
        workflowLabel: string,
        useWorkflow: boolean,
    };
}

export interface CaseDrugListInfo {
    NO: number;
    DG_DRUG_ROLE_LABEL: string;
    DG_PRODUCT_NAME: string;
    DG_BLIND: string;
    DG_DOSE: string;
    DG_GEST_PERIOD: string;
    DG_ACN_LABEL: string;
    deleteYn: string;
}

export interface CasePage {
    pageId: string;
    pageLabel: string;
    required: number;
    saved: string;
}

export interface CaseWarning {
    id: string;
    message: string;
    status: string;
}

export interface UserSponsorsRetrieveResponse {
    data: UserSponsorsRetrieveData;
}

export interface UserSponsorsRetrieveData {
    sponsorList: UserSponsorRetrieveResponse[];
}

export interface UserSendersRetrieveResponse extends BaseResponse {
    data: UserSendersRetrieveData;
}

export interface UserSendersRetrieveData {
    senders: UserSenderRetrieveResponse[];
}

export interface UserSponsorRetrieveResponse {
    sponsorKey: number;
    sponsorName: string;
    meddraLang: string;
}

export interface UserSenderRetrieveResponse {
    SENDER_KEY: number;
    SND_ORGAN: string;
}

export interface UserAuthResponse extends BaseResponse {
    data: UserAuthResponseData;
}

export interface UserAuthResponseData {
    caseNo: string;
    caseUidWorld: string;
    clientType: string;
    userId: string;
    userName: string;
    roleName: string;
    langId: string;
    meddraLang: string;
    menuList: Menu[];
    privList: Privilege[];
    organization: string;
    unblind: boolean;
    senderName: string;
    roleId: string;
    timeZone: string;
    token: string;
    useAppendix: string;
    environment: string;
    workflowStatus: string;
    useWorkflow: boolean;
}

export interface Menu {
    menuId: string;
    menuLabel: string;
    menuType: string;
}

export interface AuthenticationResponse {
    data: AuthenticationResponseData;
    success: boolean;
    errCode: number;
    errorDetails: string;
    errMsg: string;
}

export interface AuthenticationResponseData {
    sponsorList: UserSponsorRetrieveResponse[];
    token: string;
    userId: string;
    userKey: number;
}

export interface LoginPromiseDto {
    needSelectSponsor: boolean;
    session: Session;
}

export interface Session {
    userKey: number;
    userId: string;
    sponsorKey: number | string;
    sessionSenderKey: number | string;
    token: string;
    language?: string;
    appendix?: string;
    clientType?: string;
}

export interface CodesRetrieveResponse extends BaseResponse {
    data: CodesRetrieveResponseData;
}

export interface CaseItemCodesRetrieveResponse extends BaseResponse {
    data: CodeRetrieveResponse[];
}

export interface CodesRetrieveResponseData {
    codeId: string;
    codeList: CodeRetrieveResponse[];
    meddraLanguage?: string;    // MEDDRA_LANG 일 경우
    meddraVersion?: string;     // MEDDRA_LANG 일 경우(세팅 안 되어있을 시 최신 버전 가져옴)
}

export interface CodesRetrievePaginatedResponse extends BaseResponse {
    data: CodesRetrievePaginatedResponseData;
}

export interface CodesRetrievePaginatedResponseData {
    codeId: string;
    listInfo: CodeRetrieveResponse[];
}

export interface CodeRetrieveResponse {
    codeLabel: string;
    codeValue: string;
}

export interface CaseKcdItemDto {
    version: CaseItemDto;
    code: CaseItemDto;
    label: CaseItemDto;
}

export interface CaseWhoartItemDto {
    term: CaseItemDto;
    engTerm: CaseItemDto;
    arrnCode: CaseItemDto;
    seqNo: CaseItemDto;
}

export interface MeddraSelectDialogRequest {
    version: string;
    searchLanguage: string;
    keyword: string;
    width?: string;
    onSelect: (row: CaseMeddra) => void;
    onCancel: (keyword: string) => void;
    isAiCoding?: boolean;
}

export interface CaseMeddraListInfo {

}

export interface CaseMeddraRetrieveResponse extends BaseResponse {
    data: CaseMeddraRetrieveData;
}

export interface CaseMeddraRetrieveData {
    listInfo: CaseMeddra[];
    pageInfo: PageInfo;
}

export interface CaseMeddra {
    lltCode: string;
    lltName: string;
    ptCode: string;
    ptName: string;
    socCode: string;
    socName: string;
    imeFlag: 'Y' | 'N' | null;
    synoName?: string;
}

export interface CaseMeddraItemDto {
    version: CaseItemDto;
    llt: CaseItemDto;
    lltName: CaseItemDto;
    imeFlag: CaseItemDto;
}

export interface CaseItemDto {
    itemId: string;
    label?: string;
    codeLabel?: string;
    value?: string;
    originValue?: string;
    nullFlavor?: string;
    originNullFlavor?: string;
    notation?: string;
    originNotation?: string;
    rowNo: number;
    rowNo2: number;
    rowNo3: number;
    notationVisible?: boolean;
    values?: any[];
    warnings?: any[];
    options?: OptionElement[];
    propertyMap?: CaseItemProperty;
    originPropertyMap?: CaseItemProperty;
}

export interface CaseItemProperty {
    fileKey: number;
    filePath: string;
    mediaType: string;
}

export interface OptionElement {
    codeValue: any;
    codeLabel: any;
}

export const CaseItemDefault = {
    itemId: '',
    label: '',
    value: '',
    originValue: '',
    nullFlavor: '',
    originNullFlavor: '',
    notation: '',
    originNotation: '',
    rowNo: 0,
    rowNo2: 0,
    rowNo3: 0,
    notationVisible: false,
    values: [],
    warnings: [],
    options: [],
    propertyMap: {
        fileKey: '',
        filePath: '',
        mediaType: ''
    },
    originPropertyMap: {
        fileKey: '',
        filePath: '',
        mediaType: ''
    },
};

export interface ValueChangeOperator {
    itemId: string;
    value: string;
    originValue: string;
}

export interface TranslationResponse extends BaseResponse {
    data: TranslationData;
}

export interface TranslationData {
    translations: Translation[];
}

export interface Translation {
    translatedText: string;
}

export interface CaseInfoItemsReterieveResponse extends BaseResponse {
    data: CaseInfoItemsReterieveData;
}

export interface CaseInfoItemsReterieveData {
    itemList: CaseInfoItem[];
}

export interface CaseInfoItem {
    card: string;
    code: string;
    codeList: CodeRetrieveResponse[];
    codeNull: string;
    dataType: string;
    itemId: string;
    itemLabel: string;
    maxLength: string;
}

export interface CaseInfoSendersRetrieveResponse extends BaseResponse {
    data: CaseInfoSendersRetrieveData;
}

export interface CaseInfoSendersRetrieveData {
    listInfo: CaseInfoSenderListInfo[];
    pageInfo: PageInfo;
}

export interface CaseInfoSenderListInfo {
    DEL_FLAG: string;
    SENDER_KEY: number;
    SND_DEPART: string;
    SND_GIV_NAME: string;
    SND_ORGAN: string;
    SND_TYPE: string;
}

export interface CaseInfoSenderRetrieveResponse extends BaseResponse {
    data: CaseInfoSenderRetrieveData;
}

export interface CaseInfoSenderRetrieveData {
    senderKey: number;
    sndType: string;
    sndOrgan: string;
    senderNotation: any;
    regulatoryList: any[];
    senderPersonList: any[];
    sndStreet: string;
    sndCity: string;
    sndState: string;
    sndPostCode: string;
    sndCountry: string;
    sndPhone: string;
    sndFax: string;
    sndEmail: string;
    delFlag: string;
    selected?: string;
}

export interface ProductSender {
    senderKey: number;
    senderName: string;
}

export interface CaseInfoReceiversRetrieveResponse extends BaseResponse {
    data: CaseInfoReceiversRetrieveData;
}

export interface CaseInfoReceiversRetrieveData {
    listInfo: CaseInfoReceiverListInfo[];
    pageInfo: PageInfo;
}

export interface CaseInfoReceiverListInfo {
    AE_NON_SOLI: string;
    AE_SOLI: string;
    DAY_TYPE: string;
    DEL_FLAG: string;
    DESCRIPTION: string;
    MNFT_KEY: number;
    MNFT_NAME: string;
    MNFT_TYPE: string;
    MNFT_TYPE_CODE: string;
    REG_TIME: string;
    SAE_NON_SOLI: string;
    SAE_SOLI: string;
}

export interface CaseInfoReceiverRetrieveResponse extends BaseResponse {
    data: CaseInfoReceiverRetrieveData;
}

export interface CaseInfoReceiverRetrieveData {
    aeNonSoli: number;
    aeSoli: number;
    alarmEmail: string;
    alarmSms: string;
    dayType: string;
    delFlag: string;
    description: string;
    mnftId: string;
    mnftKey: number;
    mnftName: string;
    mnftType: number;
    new: boolean;
    saeNonSoli: number;
    saeSoli: number;
    consignee: Consignee[];
    selected?: string;
}

export interface Consignee {
    email: string;
    seqNo: number;
    userName: string;
    phone: string;
    delFlag: string;
}

export interface CaseInfoProductsRetrieveResponse extends BaseResponse {
    data: CaseInfoProductsData;
}

export interface CaseInfoProductsData {
    listInfo: CaseInfoProductListInfo[];
    pageInfo: PageInfo;
}

export interface CaseInfoProductListInfo {
    BRAND_NAME: string;
    DEL_FLAG: string;
    DESCRIPTION: string;
    MNFT_NAME: string;
    PRD_BRAND_NAME: string;
    PRD_KEY: number;
    PRD_NAME: string;
    REG_TIME: string;
}

export interface CaseInfoProductRetrieveResponse extends BaseResponse {
    data: CaseInfoProductData;
}

export interface CaseInfoProductData {
    brandName: string;
    delFlag: string;
    description: string;
    mnftKey?: number;
    mnftName?: string;
    senderKey?: number;
    senderName?: string;
    mnft: CaseInfoReceiverRetrieveData;
    sender: ProductSender;
    productDrug: ProductDrug;
    productDrugNotation: ProductDrugNotation;
    prdKey: number;
    prdName: string;
    substances: Substance[];
}

export interface ProductDrug {
    mpidVersion: string;
    mpid: string;
    phpidVersion: string;
    phpid: string;
    mfdsMpidVersion: string;
    mfdsMpid: string;
    productName: string;
    country: string;
    blind: string;
    authNumber: string;
    authNationCode: string;
    authName: string;
    delFlag: string;
}

export interface ProductDrugNotation {
    productName: string;
    authName: string;
}

export interface Substance {
    delFlag: string;
    rowNo: number;
    subsDose: string;
    subsDoseu: string;
    subsName: string;
    subsTid: string;
    subsTidVer: string;
    mfdsTid: string;
    mfdsTidVer: string;
}

export interface CaseInfoReportersRetrieveResponse {
    data: CaseInfoReportersRetrieveData;
}

export interface CaseInfoReportersRetrieveData {
    listInfo: CaseInfoReporterListInfo[];
    pageInfo: PageInfo;
}

export interface CaseInfoReporterListInfo {
    DEL_FLAG: string;
    REG_TIME: string;
    RPT_GIV_NAME: string;
    RPT_KEY: number;
    RPT_ORGAN: string;
    RPT_QUALIFY: string;
}

export interface CaseInfoStudiesRetrieveResponse extends BaseResponse {
    data: CaseInfoStudiesData;
}

export interface CaseInfoStudiesData {
    listInfo: CaseInfoStudyListInfo[];
    pageInfo: PageInfo;
}

export interface CaseInfoStudyListInfo {
    DEL_FLAG: string;
    PRODUCT: string;
    REG_TIME: string;
    STD_KEY: number;
    STD_NAME: string;
    STD_REACTION_TYPE: string;
    STD_SPN_NO: string;
    SYNC: string;
}

export interface SyncCdmsStudiesRetrieveResponse {
    data: SyncCdmsStudiesData;
}

export interface SyncCdmsStudiesData {
    listInfo: SyncCdmsStudyListInfo[];
    pageInfo: PageInfo;
}

export interface SyncCdmsStudyListInfo {
    studyKey: number;
    studyName: string;
    sourceStudy: string;
    protocol: string;
}

export interface SyncR3StudiesRetrieveResponse {
    data: SyncR3StudiesData;
}

export interface SyncR3StudiesData {
    listInfo: SyncR3StudyListInfo;
    pageInfo: PageInfo;
}

export interface SyncR3StudyListInfo {
    stdKey: number;
    studyNumber: string;
    studyName: string;
    targetStudy: string;
}

export interface CaseInfoStudyRetrieveResponse extends BaseResponse {
    data: CaseInfoStudyData;
}

export interface CaseInfoStudyData {
    delFlag: string;
    new: boolean;
    products: CaseInfoProductData[];
    selectedProducts: number[];
    registrations: CaseInfoStudyRegistration[];
    reporters: CaseInfoReporterListInfo[];
    stdKey: number;
    stdName: string;
    studyInfoNotation: any;
    stdReactionType: string;
    stdSpnNo: string;
    sync: string;
    stdOid: string;
}

export interface CaseInfoStudyRegistration {
    rowNo: number;
    delFlag: string;
    stdRegCountry: string;
    stdRegNo: string;
}

export interface CaseInfoReporterRetrieveResponse extends BaseResponse {
    data: CaseInfoReporterRetrieveData;
}

export interface CaseInfoReporterRetrieveData {
    new: boolean;
    rptCity: string;
    rptCountry: string;
    rptDepart: string;
    rptFamName: string;
    rptGivName: string;
    rptKey: number;
    rptMidName: string;
    rptOrgan: string;
    rptPhone: string;
    rptPostCode: string;
    rptPrimary: boolean;
    rptQualify: string;
    rptState: string;
    rptStreet: string;
    rptTitle: string;
    delFlag: string;
}

export interface CaseInfoNarrativesRetrieveResponse extends BaseResponse {
    data: CaseInfoNarrativesRetrieveData;
}

export interface CaseInfoNarrativesRetrieveData {
    listInfo: CaseInfoNarrativeListInfo[];
    pageInfo: PageInfo;
}

export interface CaseInfoNarrativeListInfo {
    NR_KEY: number;
    NAME: string;
    DEL_FLAG: string;
}

export interface CaseInfoNarrativeRetrieveResponse extends BaseResponse {
    data: CaseInfoNarrativeRetrieveData;
}

export interface CaseInfoNarrativeRetrieveData {
    nrKey: number;
    name: string;
    narrative: string;
    notation: string;
    description: string;
    delFlag: string;
}

export interface CaseInfoItemAuditTrailRetrieveResponse {
    data: CaseInfoItemAuditTrailRetrieveData;
}

export interface CaseInfoItemAuditTrailRetrieveData {
    auditTrail: AuditTrail[];
    categoryId: string;
    parentKey: number;
    rowNo: number;
    sponsorKey: number;
}

export interface AvailableToDownloadReportResponse {
    data: AvailableToDownloadReportData;
}

export interface AvailableToDownloadReportData {
    reportList: AvailableToDownloadReport;
}

export interface AvailableToDownloadReport {
    reportFormat: string;
    reportId: string;
    reportName: string;
    customizedId: string;
}

export interface FileDownloadResponse extends BaseResponse {
    data: any;
    fileName: string;
}

export interface AdminSettingsRetrieveResponse extends BaseResponse {
    data: AdminSettingsRetrieveData;
}

export interface AdminSettingsRetrieveData {
    appendixList: Appendix[];
    companyLogo: string;
    companyLogoOrigin: string;
    formatItems: FormatItems;
    meddraLang: string;
    meddraVer: string;
    notation: boolean;
    timeZone: string;
    workflow: boolean;
    workflows: Workflow[];
}

export interface AdminSettingsAuditTrailResponse {
    data: AdminSettingsAuditTrailData;
}

export interface AdminSettingsAuditTrailData {
    auditTrailList: AuditTrail[];
    itemId: string;
}

export interface AdminSettingsNumberSettingAuditTrailsResponse {
    data: AdminSettingsNumberSettingAuditTrailsData[];
}

export interface AdminSettingsNumberSettingAuditTrailsData {
    elementId: string;
    numberSetting: string;
    reason: string;
    regTime: string;
    userId: string;
}

export interface AdminUsersRetrieveResponse {
    data: AdminUsersRetrieveData;
}

export interface AdminUsersRetrieveData {
    listInfo: AdminUserListInfo[];
    pageInfo: PageInfo;
}

export interface AdminUserListInfo {
    DEL_FLAG: string;
    DESCRIPTION: string;
    ENDTC: string;
    PHONE: string;
    REG_TIME: string;
    ROLE_NAME: string;
    STDTC: string;
    USER_ID: string;
    USER_KEY: number;
    USER_NAME: string;
}

export interface AdminUserRetrieveResponse extends BaseResponse {
    data: AdminUserRetrieveData;
}

export interface AdminUserRetrieveData {
    description: string;
    unblind: boolean;
    endtc: string;
    phone: string;
    roles: Role[];
    stdtc: string;
    senders: CaseInfoSenderRetrieveData[];
    studies: CaseInfoStudyData[];
    products: CaseInfoProductData[];
    roleId?: string;
    userId: string;
    userKey: number;
    userName: string;
}

export interface AdminUserAuditTrailsResponse {
    data: AdminUserAuditTrailsData;
}

export interface AdminUserAuditTrailsData {
    auditTrailList: AuditTrail[];
    itemId: string;
}

export interface SystemAdminReceiverRetrieveResponse extends BaseResponse {
    data: SystemAdminReceiverRetrieveData;
}

export interface SystemAdminReceiverRetrieveData {
    organ: string;
    organNotation: string;
    title: string;
    titleNotation: string;
    familyName: string;
    familyNameNotation: string;
    givenName: string;
    givenNameNotation: string;
}

export interface AdminRolesRetrieveResponse extends BaseResponse {
    data: AdminRolesRetrieveData;
}

export interface AdminRolesRetrieveData {
    listInfo: AdminRolesListInfo[];
    pageInfo: PageInfo;
}

export interface AdminRolesListInfo {
    DEL_FLAG: string;
    DESCRIPTION: string;
    ROLE_ID: string;
    ROLE_NAME: string;
    appended: boolean;
}

export interface AdminRolesAuditTrailsResponse {
    data: AdminRolesAuditTrailsData;
}

export interface AdminRolesAuditTrailsData {
    auditTrailList: AuditTrail[];
    roleId: string;
}

export interface Role {
    roleId: string;
    roleName: string;
    selected: string;
}

export interface Privilege {
    menuName: string;
    privId: string;
    privName: string;
    privType: string;
}

export interface Appendix {
    appendixCode: string;
    appendixName: string;
    delFlag: string;
    reason: string;
}

export interface FormatItems {
    format: string;
    header: string;
    identifier: string;
    itemList: any[];
    reason: string;
    sequence: number;
    sequenceType: string;
}

export interface Workflow {
    rowNo: number;
    workflowLabel: string;
    workflowKey: number;
    roles: AdminRolesListInfo[];
    due: string;
    description: string;
    delFlag: string;
    saveKey?: number;
}

export interface WorkflowRetrieveResponse {
    workflowKey: number;
    workflowLabel: string;
}

export interface WorkflowRole {
    roleId: string;
    roleName: string;
}

export interface ProductsRetrieveResponse {
    data: ProductsRetrieveData;
}

export interface ProductsRetrieveData {
    listInfo: ProductListInfo[];
    pageInfo: PageInfo;
}

export interface ProductListInfo {
    BRAND_NAME: string;
    DEL_FLAG: string;
    DESCRIPTION: string;
    MNFT_NAME: string;
    PRD_BRAND_NAME: string;
    PRD_KEY: number;
    PRD_NAME: string;
    REG_TIME: string;
}

export interface StudiesRetrieveResponse {
    data: StudiesRetrieveData;
}

export interface StudiesRetrieveData {
    listInfo: StudyListInfo[];
    pageInfo: PageInfo;
}

export interface StudyListInfo {
    DEL_FLAG: string;
    PRODUCT: string;
    REG_TIME: string;
    STD_KEY: number;
    STD_NAME: string;
    STD_REACTION_TYPE: string;
    STD_SPN_NO: string;
    SYNC: string;
}

export interface MappingRuleSummaryRetrieveResponse extends BaseResponse {
    data: MappingRuleSummaryRetrieveData;
}

export interface MappingRuleSummaryRetrieveData {
    listInfo: MappingRuleSummaryListInfo[];
    pageInfo: PageInfo;

}

export interface MappingRuleSummaryListInfo {
    CODE_ID: string;
    DEL_FLAG: string;
    TARGET_CONST: string;
    TARGET_DELIMITER: string;
    TARGET_ELEMENT_ID: string;
    TARGET_FUNCTION: string;
    TARGET_ITEM: string;
    TARGET_ITEM_ID: string;
    TARGET_MSG_ID: string;
    TARGET_PAGE: string;
    TARGET_PAGE_ID: string;
    TARGET_SEQ_NO: number;
    UI_TYPE: string;
    reason: string;
    syncInfoCodes: SyncInfoCodeList[];
    syncInfoItems: SyncInfoItemList[];
    targetKey: number;
}

export interface SyncInfoCodeList {
    CODE_DEL_FLAG: string;
    SEQ_NO_CODE: number;
    SOURCE_VALUE: string;
    TARGET_VALUE: string;
}

export interface SyncInfoItemList {
    CODE_KEY: string;
    CYCLE_NO: number;
    DELIMITER: string;
    FUNCTION: string;
    FUNCTION_ARGV: string;
    ITEM: string;
    ITEM_DEL_FLAG: string;
    ITEM_KEY: number;
    PAGE: string;
    PAGE_VERSION: string;
    SEQ_NO: number;
    VER_KEY: number;
    VISIT: string;
    VISIT_KEY: number;
    VISIT_NO: number;
    visits: MappingRuleCDMSVisitCodeRetrieveData[];
}

export interface MappingRuleCDMSVisitCodeRetrieveResponse {
    data: MappingRuleCDMSVisitCodeRetrieveData[];
}

export interface DuplicateSubjectsResponse extends BaseResponse {
    data: string[];
}

export interface MappingRuleCDMSVisitCodeRetrieveData {
    cycleNo: number;
    visit: string;
    visitKey: number;
    visitNo: number;
}

export interface MappingRuleSyncTargetAuditTrailResponse {
    data: MappingRuleSyncTargetAuditTrailRetrieveData;
}

export interface MappingRuleSyncTargetAuditTrailRetrieveData {
    localDateTime: string;
    reason: string;
    targetFunction: string;
    targetValue: string;
    time: string;
    userId: string;
}

export interface AdminRolePrivilegeRetrieveResponse extends BaseResponse {
    data: AdminRolePrivilegeRetrieveData;
}

export interface AdminRolePrivilegeRetrieveData {
    privileges: Privilege[];
    roles: any[];
    rolePrivilegesMap: any;
}

export interface DataHistoriesRetrieveResponse {
    data: DataHistoriesRetrieveData;
}

export interface DataHistoriesRetrieveData {
    listInfo: DataHistoriesListInfo[];
    pageInfo: PageInfo;
}

export interface DataHistoriesListInfo {
    ACT_TYPE: string;
    DATA_FILE_KEY: number;
    DATA_KEY: number;
    ENDTC: string;
    ERROR_FILE_KEY: number;
    RPT_TYPE: string;
    STATUS: string;
    STDTC: string;
    USER_ID: string;
    WARNING_DATA_KEY: number;
    ICSRS?: number;
    MFDS_REPORT_TYPE: string;
    EMA_REPORT_TYPE: string;
    FDA_REPORT_TYPE: string;
    PRD_NAME: string;
    WARNING_FILE_KEY: number;
    IS_IMPORT: boolean;
    IS_ERROR: boolean;
}

export interface DataHistoryImportDetailListInfo {
    DATA_KEY: number;
    CASE_NO: string;
    CASE_REG_TIME: string;
    CASE_RECENT_DTC: string;
    WARNING_DATA_KEY: string;
    CASE_KEY: string;

}

export interface MonitoringSearchItemsRetrieveResponse {
    data: MonitoringSearchItemsInfo[];
}

export interface MonitoringSearchItemsInfo {
    codeList: CodeRetrieveResponse[];
    itemId: string;
    itemLabel: string;
    uiType: string;
}

export interface MonitoringHistoriesRetrieveResponse {
    data: MonitoringHistoriesRetrieveData;
}

export interface MonitoringHistoriesRetrieveData {
    listInfo: MonitoringHistoriesListInfo[];
    pageInfo: PageInfo;
}

export interface MonitoringHistoriesListInfo {
    ACT_TYPE: string;
    DEL_FLAG: string;
    ENDTC: string;
    MON_KEY: number;
    STATUS: string;
    STDTC: string;
    TOTAL_RECORDS: number;
    USER_ID: string;
}

export interface CompaniesRetrieveResponse {
    data: CompaniesRetrieveData;
}

export interface CompaniesRetrieveData {
    listInfo: CompaniesListInfo[];
    pageInfo: PageInfo;
}

export interface CompaniesListInfo {
    CLIENT_TYPE: string;
    COUNTRY: string;
    DEL_FLAG: string;
    INCL_COUNTRY: string;
    LANGUAGE: string;
    MEDDRA_LANG: string;
    REG_TIME: string;
    SPN_KEY: number;
    SPN_NAME: string;
    STATUS: string;
    TIME_ZONE: string;
    USER_ID: string;
}

export interface CompanyRetrieveResponse extends BaseResponse {
    data: CompanyRetrieveData;
}

export interface CompanyRetrieveData {
    spnName: string;
    clientType: string;
    status: string;
    country: string;
    description: string;
    email: string;
    phone: string;
    userName: string;
}

export interface CompanyPropertyAuditTrailsResponse {
    data: CompanyPropertyAuditTrailsData;
}

export interface CompanyPropertyAuditTrailsData {
    auditTrailList: AuditTrail[];
    itemId: string;
}

export interface StudyMappingsRetrieveResponse {
    data: StudyMappingsRetrieveData;
}

export interface StudyMappingsRetrieveData {
    listInfo: StudyMappingsListInfo[];
    pageInfo: PageInfo;
}

export interface StudyMappingsListInfo {
    DEL_FLAG: string;
    REG_TIME: string;
    SOURCE_STUDY: string;
    STD_KEY: number;
    STUDY_KEY: number;
    TARGET_STUDY: string;
}

export interface StudyMappingAuditTrailsResponse {
    data: StudyMappingAuditTrail[];
}

export interface StudyMappingAuditTrail {
    SOURCE_STUDY: string;
    TARGET_STUDY: string;
    delFlag: string;
    reason: string;
    regTime: string;
    stdKey: number;
    studyKey: number;
    userId: string;
}

export interface SystemSyncStudiesRetrieveResponse {
    data: SyncStudiesRetrieveData;
}

export interface SyncStudiesRetrieveData {
    listInfo: SyncStudiesListInfo[];
    pageInfo: PageInfo;
}

export interface SyncStudiesListInfo {
    INPUT_TIME: string;
    LAST_RELEASE_DATE: string;
    MAPPING_FLAG: string;
    STD_SPN_NO: string;
    STUDY_ID: string;
    stdKey: number;
    studyKey: number;
}

export interface CDMSItemSelectDialogRequest {
    index: number;
}

export interface SyncCubeCDMSItemRetrieveResponse extends BaseResponse {
    data: SyncCubeCDMSItemRetrieveData[];
}

export interface SyncCubeCDMSItemRetrieveData {
    listInfo: SyncCubeCDMSItemListInfo[];
    pageInfo: PageInfo;
}

export interface SyncProgressDoneCheckRequest {
    aeSeq: number;
    schdKey: number;
    subjKey: number;
}

export interface SyncCubeCDMSItemListInfo {
    AEENDTC: string;
    AESEQ: number;
    AESTDTC: string;
    AETERM: string;
    REPORT_YEAR: number;
    SAE: string;
    SCHD_KEY: number;
    SITE: string;
    STUDY_KEY: number;
    SUBJ_ID: string;
    SUBJ_KEY: number;
    SYNC: string;
    SYNC_KEY: string;
    TOTAL_CNT: number;
    CHECKED?: boolean;
    SEQ_NO?: number;
    SYNC_STATUS?: string;
    BOOKLET_STATUS?: string;
    BOOKLET_STATUS_CODE?: string;
    VISIT_LABEL: string;
    IME: string;
}

export interface SyncCubeCDMSItemData {
    selectedSyncItemData: SyncCubeCDMSItemListInfo;
    bookletVisible: boolean;
}

export interface StudyTargetCodesRetrieveResponse {
    data: StudyTargetCodeInfo[];
}

export interface StudyTargetCodeInfo {
    stdKey: string;
    targetStudy: string;
}

export interface StudySourceCodesRetrieveResponse {
    data: StudySourceCodeInfo[];
}

export interface StudySourceCodeInfo {
    studyKey: string;
    sourceStudy: string;
}

export interface CompanyStudySyncRetrieveResponse extends BaseResponse {
    data: CompanyStudySyncRetrieveData;
}

export interface CompanyStudySyncRetrieveData {
    DEL_FLAG: string;
    REG_TIME: string;
    SOURCE_STUDY: string;
    STD_KEY: number;
    STUDY_KEY: number;
    TARGET_STUDY: string;
}

export interface DataExportRetrieveReceiversResponse {
    data: DataExportRetrieveReceiversData;
}

export interface DataExportRetrieveReceiversData {
    receiverList: DataExportReceiverInfo[];
}

export interface DataExportReceiverInfo {
    receiverType: string;
    receiverName: string;
    ema: boolean;
    valid: boolean;
}

export type PMDAReportType = '治験' | '市販後';

export interface DataExportRetrieveCasePreviewsResponse {
    data: DataExportRetrieveCasePreviewsData;
}

export interface DataExportRetrieveCasePreviewsData {
    listInfo: DataExportCasePreviewInfo[];
    pageInfo: PageInfo;
}

export interface DataExportCasePreviewInfo {
    AE_TERM: string;
    CASE_KEY: number;
    CASE_NO: string;
    CASE_RECENT_DTC: string;
    CASE_RPT_TYPE: string;
    IS_SAE: string;
    PRD_NAME: string;
    SUBJECT: string;
    totalCount: number;
}

export interface DataLibrariesRetrieveResponse extends BaseResponse {
    data: DataLibrariesRetrieveData;
}

export interface DataLibrariesRetrieveData {
    listInfo: DataLibrariesListInfo[];
    pageInfo: PageInfo;
}

export interface DataLibrariesListInfo {
    DEL_FLAG: string;
    DESCRIPTION: string;
    REG_TIME: string;
    SCH_KEY: number;
    SCH_NAME: string;
    SEARCH_ITEM: any[];
    new: boolean;
}

export interface DataLibraryRetrieveResponse extends BaseResponse {
    data: DataLibrariesListInfo;
}

export interface DataPageItemsResponse extends BaseResponse {
    data: DataPageItemsInfo[];
}

export interface DataPageItemsInfo {
    codeList: CodeRetrieveResponse[];
    itemList: DataLibrarySearchItemsInfo[];
    pageId: string;
    pageLabel: string;
}

export interface DataLibrarySearchItemsInfo {
    codeList: CodeRetrieveResponse[];
    itemId: string;
    itemLabel: string;
    uiType: string;
}

export interface DataLibraryAuditTrailsResponse {
    data: AuditTrail[];
}

export interface DataSearchItemAuditTrailsResponse {
    data: SearchItemAuditTrail[];
}

export interface SearchItemAuditTrail {
    condition: string;
    conditionLabel: string;
    delFlag: string;
    itemId: string;
    itemLabel: string;
    no: number;
    pageId: string;
    pageLabel: string;
    reason: string;
    schKey: number;
    seqNo: number;
    time: string;
    userId: string;
    value: string;
    valueMax: string;
}

export interface CubeCDMSItemsRetrieveResponse {
    data: CubeCDMSItemChildren[];
}

export interface CubeCDMSItemChildren {
    id: string;
    label: string;
    property: CubeCDMSItemProperty;
    type: string;
    children: CubeCDMSItemChildren[];
}

export interface CubeCDMSItemProperty {
    itemKey?: number;
    pageLabel?: string;
    verKey?: number;
    type?: string;
}

export interface PageInfo {
    menuId: string;
    pageNo: number;
    rowCount: number;
    innerRowCount?: number;
    totalCount: number;
    totalPage: number;
}

export interface SubmissionSendersRetrieveResponse {
    data: SubmissionSenderListInfo[];
}

export interface SubmissionSenderListInfo {
    senderKey: number;
    senderOrgan: string;
}

export interface SubmissionSearchItemsRetrieveResponse {
    data: SubmissionSearchItemsInfo[];
}

export interface SubmissionSearchItemsInfo {
    codeList: CodeRetrieveResponse[];
    itemList: SubmissionSearchItemsItemList[];
    pageId: string;
    pageLabel: string;
}

export interface SubmissionSearchItemsItemList {
    codeList: CodeRetrieveResponse[];
    itemId: string;
    itemLabel: string;
    uiType: string;
}

export interface SubmissionCheckResponse {
    data: SubmissionCheckInfo[];
}

export interface SubmissionCheckInfo {
    batchKey: number;
}

export interface SubmissionCheckListResponse {
    data: SubmissionCheckListInfo[];
}

export interface SubmissionCheckListInfo {
    listInfo: SubmissionCheckListInfo[];
    pageInfo: PageInfo[];
}

export interface SubmissionCheckListInfo {
    BATCH_KEY: number;
    CASE_KEY: number;
    CASE_NO: string;
    CASE_REG_TIME: string;
    LAST_FU: string;
    PRD_NAME: string;
    PREV_BATCH_NO: string;
    PREV_RECEIVER: string;
    CHECKED?: boolean;
}

export interface SubmissionBatchHistoryRetrieveResponse {
    data: SubmissionBatchHistoryRetrieveData;
}

export interface SubmissionBatchHistoryRetrieveData {
    listInfo: SubmissionBatchHistoryListInfo[];
    pageInfo: PageInfo[];
}

export interface SubmissionBatchHistoryListInfo {
    ACK_CREATION_TIME: string;
    ACK_FILE_KEY: number;
    ACK_STATUS: string;
    BATCH_KEY: number;
    BATCH_STATUS: string;
    BAT_FILE_KEY: number;
    ERR_FILE_KEY: number;
    INPUT_TIME: string;
    MDN_FILE_KEY: number;
    REG_TIME: string;
    TRN_BATCH_NO: string;
    TRN_RECEIVER: string;
    ICSRS: number;
    COMMENTS: string;
    USER_ID: string;
    WAR_DATA_KEY: number;
    WAR_FILE_KEY: number;
}

export interface SubmissionBatchHistoryDetailRetrieveResponse {
    data: SubmissionBatchHistoryDetailRetrieveData;
}

export interface SubmissionBatchHistoryDetailRetrieveData {
    listInfo: SubmissionBatchHistoryDetailListInfo[];
    pageInfo: PageInfo[];
}

export interface SubmissionBatchHistoryDetailListInfo {
    ACK_MESSAGE: string;
    ACK_STATUS: string;
    BATCH_KEY: string;
    CASE_KEY: number;
    WAR_DATA_KEY: number;
    CASE_NO: string;
    CASE_RECENT_DTC: string;
    INPUT_TIME: string;
    TRN_BATCH_NO: string;
    LOCAL_REPORT_NUM: string;
}

export interface SubmissionCaseHistoryRetrieveResponse {
    data: SubmissionCaseHistoryRetrieveData;
}

export interface SubmissionCaseHistoryRetrieveData {
    listInfo: SubmissionCaseHistoryListInfo[];
    pageInfo: PageInfo[];
}

export interface SubmissionCaseHistoryListInfo {
    AS2_STATE: string;
    BATCH_TYPE: string;
    BAT_FILE_KEY: number;
    CASE_KEY: string;
    CASE_NO: string;
    CASE_RECENT_DTC: string;
    ERR_FILE_KEY: number;
    INPUT_TIME: string;
    TRN_BATCH_NO: string;
    USER_ID: string;
    WARNING_COUNT: number;
    WAR_FILE_KEY: number;
}

export interface SubmissionSubmitResponse {
    data: SubmissionSubmitData;
}

export interface SubmissionSubmitData {
    batchKey: number;
}

export interface SubmissionCaseFilterRetrieveResponse {
    data: SubmissionCaseFilterRetrieveData;
}

export interface SubmissionCaseFilterRetrieveData {
    searchItem: SearchItem[];
}

export interface SearchItem {
    DESCRIPTION: string;
    SCH_KEY: number;
    SCH_NAME: SelectedSearchItem[];
    new: boolean;
    DEL_FLAG: string;
}

export interface SelectedSearchItem {
    codeList: CodeRetrieveResponse[];
    condition: string;
    itemId: string;
    pageId: string;
    reason: string;
    seqNo: number;
    value: string;
    valueLabel?: string;
    valueMax: string;
    valueMaxLabel?: string;
    delFlag: string;
}

export interface UserTrace {
    userId: string;
    route: any;
    permit: boolean;
}

export interface CaseRepeatPageRow extends BaseResponse {
    data: CaseRepeatPageRowData;
}

export interface CaseRepeatPageRowData {
    firstRowNo: number;
    currentRowNo: number;
    lastRowNo: number;
    previousRowNo: number;
    nextRowNo: number;
}

export interface ExcelExportResponse extends BaseResponse {
    data: ExcelExportResponseData;
}

export interface ExcelExportResponseData {
    dataKey: number;
}

export interface FileSizeLimit {
    bytes: number;
    unit: string;
}

export interface CaseStatusData {
    statusId: string;
    status: string;
    reason?: string;
}

export interface CaseStatusResponse extends BaseResponse {
    data: CaseStatusData;
}

export interface NullDataResponse extends BaseResponse {
    data: null;
}

export interface CaseKeys {
    caseKeys: number[];
}

export interface ResetAEInfo {
    syncKey: number | string;
    subjKey: number | string;
    aeSeq: number | string;
}

export interface ResetSyncRequest {
    sponsorKey: number | string;
    stdKey: number | string;
    resetAeInfos: ResetAEInfo[];
    reason: string;
}

export interface SyncInfo {
    aeSeq: number;
    schdKey: number;
    subjKey: number;
}

export interface SyncAuditTrail {
    columnLabel: string;
    value: string;
    reason: string;
    dateTime: string;
    userId: string;

}

export interface SyncAuditTrailResponse extends BaseResponse {
    data: SyncAuditTrail[];
}

export interface SyncAuditTrailRequest {
    syncInfo: SyncInfo;
    sponsorKey: string | number;
    stdKey: number | string;
}

export interface SelectedAeSyncsRequest {
    SUBJ_KEY: number;
    SUBJ_SYNC: SubjectSync[];
}

export interface SubjectSync {
    SCHD_KEY: number;
    AESEQ: number;
    AETERM: string;
    SAE: string;
    SEQ_NO: number;
}

export interface AdminFlag {
    isAdmin: boolean;
}

export interface AdminPrivilegeResponse extends BaseResponse {
    data: AdminFlag;
}

export interface UserKey {
    userKey: string | number;
}

export interface LogoutResponse extends BaseResponse {
    data: UserKey;
}

export interface AuditTrailRequest {
    itemId: string;
    userKey?: string | number;
    sponsorKey?: string | number;
}

export interface BaseListRequest {
    pageNo: string | number;
    rowCount: string | number;
    fullText: string;
}

export interface SearchHistory {
    CATEGORY: string;
    VALUE: string;
    USER_ID: string;
    DATE: string;
}

export interface SearchHistoryResponse extends BaseResponse {
    data: SearchHistory[];
}

export interface PopupNoticeInfo {
    noticeKey: number;
    country: string;
    visible?: boolean;
}
export interface PopupNoticesDialogRequest {
    popupNoticeInfos: PopupNoticeInfo[];
}
export interface PopupNoticesResponse extends BaseResponse {
    data: PopupNoticesDialogRequest;
}

export class CommonModelUtils {
    public static isRenderableTableHeader(header: TableHeader): boolean {
        return header.visible === 'Y';
    }

    public static getBoolean(value: any): boolean {
        return (value === 'true' || value === true || value === 'Y');
    }

    public static getOrDefault(value: any, defaultValue: any): any {
        return value === undefined || value === null ? defaultValue : value;
    }

    public static getOrDefaultPaginate(data: any) {
        return data === null ? {listInfo: [], pageInfo: {}} : data;
    }

    public static toLowerCase(value: any) {
        if (value === null || value === undefined) {
            return value;
        }

        return value.toString().toLowerCase();
    }

    public static compareKey(key1: any, key2: any) {
        key1 = key1 && typeof key1 === 'number' ? key1.toString() : key1;
        key2 = key2 && typeof key2 === 'number' ? key2.toString() : key2;
        return key1 === key2;
    }
}


export interface ProductCodingList {
    CASE_KEY: number;
    CASE_NO: string;
    CASE_REG_TIME: string;
    ROW_NO: number;
    DG_PRODUCT_NAME: string;
    RECOMMENDED: string | null;
    DRUG_NAME: string;
    INGREDIENTS: string;
    COUNTRY: string;
    PHARM_FORM: string;
    STRENGTH: string;
    CHECKED?: boolean;
}

export interface ProductCodingListRetrieveData {
    listInfo: ProductCodingList[];
    pageInfo: PageInfo[];
}
export interface ProductCodingListRetrieveResponse extends BaseResponse {
    data: ProductCodingListRetrieveData;
}

export interface ProductCodingUpdateResponse extends BaseResponse {
    data: number;
}
export interface WhoddVersionRetrieveResponse extends BaseResponse {
    data: string;
}

export interface ProductRecommendedCodeInfoResponse extends BaseResponse {
    data: WhoddListInfo;
}

export interface WhoddSubstanceCodingList {
    CASE_KEY: number;
    CASE_NO: string;
    CASE_REG_TIME: string;
    ROW_NO: number;
    ROW_NO2: number;
    DG_SUBSTANCE_NAME: string;
    RECOMMENDED: string | null;
    SUBSTANCE_NAME: string;
    CHECKED?: boolean;
}

export interface WhoddSubstanceCodingListRetrieveData {
    listInfo: WhoddSubstanceCodingList[];
    pageInfo: PageInfo[];
}

export interface WhoddSubstanceCodingListRetrieveResponse extends BaseResponse {
    data: WhoddSubstanceCodingListRetrieveData;
}

export interface WhoddSubstanceCodingUpdateResponse extends BaseResponse {
    data: number;
}

export interface WhoddSubstanceRecommendedCodeInfoResponse extends BaseResponse {
    data: WhoddCasListInfo;
}

export interface MfdsProductCodingList {
    CASE_KEY: number;
    CASE_NO: string;
    CASE_REG_TIME: string;
    ROW_NO: number;
    DG_PRODUCT_NAME: string;
    RECOMMENDED: string | null;
    KOREAN_TERM: string;
    STATUS: string;
    CHECKED?: boolean;
}

export interface MfdsProductCodingListRetrieveData {
    listInfo: MfdsProductCodingList[];
    pageInfo: PageInfo[];
}

export interface MfdsProductCodingListRetrieveResponse extends BaseResponse {
    data: MfdsProductCodingListRetrieveData;
}

export interface MfdsProductCodingUpdateResponse extends BaseResponse {
    data: number;
}

export interface MfdsProductRecommendedCodeInfoResponse extends BaseResponse {
    data: MfdsProductListInfo;
}

export interface MfdsSubstanceCodingList {
    CASE_KEY: number;
    CASE_NO: string;
    CASE_REG_TIME: string;
    ROW_NO: number;
    ROW_NO2: number;
    DG_SUBSTANCE_NAME: string;
    RECOMMENDED: string | null;
    TERM: string;
    TERM_ENG: string;
    CHECKED?: boolean;
}

export interface MfdsSubstanceCodingListRetrieveData {
    listInfo: MfdsSubstanceCodingList[];
    pageInfo: PageInfo[];
}

export interface MfdsSubstanceCodingListRetrieveResponse extends BaseResponse {
    data: MfdsSubstanceCodingListRetrieveData;
}

export interface MfdsSubstanceCodingUpdateResponse extends BaseResponse {
    data: number;
}

export interface MfdsSubstanceRecommendedCodeInfoResponse extends BaseResponse {
    data: MfdsSubstanceListInfo;
}

export interface WorkflowPrivilegeResponse extends BaseResponse {
    data: {
        workflowPrivilege: boolean;
    };
}
export interface WorkflowBatchInfoRequest extends BaseResponse {
    data: {
        workflowInfo: WorkflowInfo;
        workflows: Workflow[];
        requestTime: string;
        userRoleId: string
    };
}

export interface WorkflowInfo {
    workflowKey: number;
    workflowLabel: string;
    roleId: string;
    userKey: string;
    roleName?: string;
    userName?: string;
    userId?: string;
    requestTime: string;
}
